import { useEffect, type ReactNode } from 'react'
import { dispatchHotkey, dispatchScan } from '@/app/hotkey-bus'
import { emptyScan, feedScanner, resolveKey } from '@/shared/domain/hotkeys'

/// Один слухач на window, і вся розкладка — у домені.
///
/// Чому не хук на екрані: клавіші глобальні за визначенням («F1 з будь-якого
/// місця відкриває чекін»), а два слухачі на F5 — це два різні розуміння того,
/// що зараз означає «готівка».
export const HotkeysProvider = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    let scanState = emptyScan()

    const handleKeyDown = (event: KeyboardEvent) => {
      // Сканер має пріоритет: серія символів швидше 30 мс — це картка, і її
      // цифри НЕ повинні доїхати до обробника «1–8».
      const scan = feedScanner(scanState, event.key, event.timeStamp)
      scanState = scan.state

      if (scan.kind === 'code') {
        event.preventDefault()
        dispatchScan(scan.code)
        return
      }
      // Усередині швидкої серії клавіші не означають дій — інакше скан «1234»
      // додасть у кошик чотири позиції.
      if (scan.kind === 'scanning' && scanState.buffer.length > 1) return

      const resolved = resolveKey(event)
      if (!resolved) return
      // Над відкритою модалкою глобальних клавіш немає.
      //
      // Слухач висить на `window`, а Radix на Escape робить лише
      // `preventDefault` без `stopPropagation` — тож той самий Esc, яким
      // рецепція закривала діалог, доїжджав до екрана під ним. З каси це
      // означало `cancel()` до кінця каскаду і **мовчки стертий кошик**; зі
      // списку клієнтів — знятий вибір сорока людей без undo. Enter був ще
      // гірший: над формою «Новий клієнт», відкритою з чекіну, він долітав до
      // `submit()` і **записував прохід зі списанням візиту** тій людині, що
      // стояла у вердикті.
      //
      // Гардити кожен екран окремо не можна за побудовою: каса звіряється зі
      // СВОЇМ `overlay` і про діалог з оболонки («Потребує уваги») не знає.
      // Тому правило одне і живе тут: поки на екрані модалка, вона і є
      // адресатом клавіш — свої вона слухає сама, локальним `onKeyDown`.
      //
      // Сканер вище цієї перевірки навмисно: у формі «Новий клієнт» є поле
      // «Картка / RFID-браслет», і зчитувач мусить у неї потрапити.
      // ПОРЯДОК ТУТ — І Є ПРАВИЛО, і воно було зламане рівно один раз.
      //
      // Перехоплення йде ПЕРШИМ, гард модалки — другим. Інакше над відкритим
      // діалогом F5 повертається браузеру і перезавантажує сторінку разом із
      // набраним кошиком (він у стані компонента), F12 відкриває DevTools
      // поверх каси, F3 — пошук по сторінці. Тобто гард, поставлений заради
      // того, щоб клавіша нічого не робила, віддавав її тому, хто зробить
      // гірше.
      //
      // `hotkeys.ts` називає це першим зі своїх двох правил: «F-ряд
      // перехоплюється ЗАВЖДИ — інакше F5 перезавантажує сторінку з непорожнім
      // кошиком». Один рядок нижче — і правило перестало виконуватись саме
      // тоді, коли на екрані діалог.
      if (resolved.preventDefault) event.preventDefault()
      if (document.querySelector('[role="dialog"][data-state="open"]')) return
      dispatchHotkey(resolved.action, resolved.slot === undefined ? {} : { slot: resolved.slot })
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return children
}
